<template>
  <div class="exercise">
    <toolbar v-if="!hideBack" transparent fixed :logo="false">
      <template v-slot:right>
        <toolbar-add-to-schedule-button
          class="mr-1"
          :item-id="id"
          item-type="Exercise"
          page-content-type="exercise"
        />
        <toolbar-bookmark-button :page-id="page_id" />
      </template>
    </toolbar>

    <template v-if="isPresent(banner) || isPresent(video)">
      <hero
        :image="banner"
        :video="video"
        :style-options="{ heightRatio: 150 }"
        :video-options="{ autoplay: true, loop: true }"
        safe-padding
      />
    </template>
    <template v-else-if="!hideBack">
      <div class="v-toolbar__spacer"></div>
    </template>

    <text-block-header :heading="title" :content="description" />

    <div
      v-if="formSubmissions.length > 0"
      class="exercise__results pt-2 bg-white"
    >
      <h3 class="px-2 mt-0">
        {{ $t("components.pageTemplates.exerciseTemplate.results") }}
      </h3>

      <collapsable
        v-for="(submission, index) in formSubmissions"
        :key="index"
        :collapsed="index !== 0"
      >
        <template v-slot:persistant>
          <div class="px-1 py-2 d-flex align-items-start">
            <v-button
              class="px-1 pt-0 pb-1 mr-1"
              ghost
              icon
              @click.stop="() => showSubmission(submission)"
            >
              <v-icon class="text-grey-60" icon="add_chart" size="1.2rem" />
            </v-button>

            <div class="text-grey-80">
              <div>
                <b>{{ submission.form.title }}</b>
              </div>

              <small class="text-grey-60">
                {{ toDate(submission.created_at) }}
                {{ $t("components.pageTemplates.exerciseTemplate.at") }}
                {{ toTime(submission.created_at, "hh:mm") }}
              </small>
            </div>
          </div>
        </template>

        <div
          class="exercise__result"
          v-for="(group, mIndex) in submission.form_groups"
          :key="mIndex"
        >
          <div class="font-weight-bold text-grey-80 py-2">
            {{ group.title }}:
          </div>

          <form-submission-group
            class="pl-1"
            :values="submission.values"
            :group="group"
          />
        </div>
      </collapsable>
    </div>

    <infinite-pagination
      v-if="isAuthenticated"
      list-key="form_submissions"
      :path="`/submissions/by_exercise/${id}`"
      :wait-seconds="MODAL_ANIMATION_SECONDS"
      @loaded="handleLoaded"
    />

    <form-submissions-modal
      v-if="selectedSubmission"
      :form-id="selectedSubmission.form_id"
      :header="
        `${selectedSubmission.title} ${$t(
          'components.pageTemplates.exerciseTemplate.results'
        )}`
      "
    />

    <v-made-with-movement v-if="!isDesktop" />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex"

import { isPresent } from "@/lib/utils"
import { toDate, toTime } from "@/lib/date-utils"
import EventBus, { EVENTS } from "@/lib/event-bus"

import Toolbar from "@/components/Toolbar"
import Hero from "@/components/Hero"
import TextBlockHeader from "@/components/TextBlockHeader"
import ToolbarBookmarkButton from "@/components/toolbar/BookmarkButton"
import ToolbarAddToScheduleButton from "@/components/toolbar/AddToScheduleButton"
import InfinitePagination from "@/components/InfinitePagination"
import Collapsable from "@/components/Collapsable"
import FormSubmissionsModal from "@/components/modals/FormSubmissionsModal"
import FormSubmissionGroup from "@/components/form-submission/RowGroup"

export default {
  components: {
    Toolbar,
    ToolbarBookmarkButton,
    ToolbarAddToScheduleButton,
    Hero,
    TextBlockHeader,
    InfinitePagination,
    Collapsable,
    FormSubmissionsModal,
    FormSubmissionGroup
  },

  props: {
    id: { type: Number, required: true },
    page_id: { type: Number, required: true },
    title: { type: String, default: "" },
    description: { type: String, default: "" },
    banner: { type: Object, default: () => ({}) },
    video: { type: Object, default: null },
    hideBack: { type: Boolean, default: false }
  },

  head() {
    return {
      title: this.title
    }
  },

  data() {
    return {
      formSubmissions: [],
      selectedSubmission: null,
      MODAL_ANIMATION_SECONDS: 200
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapState("responsive", ["isDesktop"])
  },

  methods: {
    toDate,
    toTime,
    isPresent,

    handleLoaded({ list }) {
      this.formSubmissions = [...this.formSubmissions, ...list]
    },

    showSubmission(submission) {
      this.selectedSubmission = submission

      this.$nextTick(() =>
        EventBus.$emit(
          EVENTS.SHOW_FORM_SUBMISSION,
          submission.id,
          submission.form_id
        )
      )
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.exercise__results {
  .collapsable {
    border-top: 1px solid $color-ui--grey-20;

    .v-icon {
      color: $color-ui--grey-80;
    }

    b {
      flex-shrink: 0;
    }
  }

  .space-pre {
    white-space: pre-wrap;
  }

  .exercise__result {
    border-top: 1px solid $color-ui--grey-20;
    margin-bottom: $size--4;
    margin-left: $size--4;

    &:first-child {
      border-top: none;
    }
  }

  .exercise__result-round {
    margin-left: $size--9;
    border-top: 1px solid $color-ui--grey-20;

    .exercise__result-round-inner {
      flex-grow: 1;
      line-height: $font-size--large;
    }

    &:first-child {
      border-top: none;
    }
  }
}
</style>
